// ─── Tier Toggle (A+ / B+) — 2 circular buttons ───
// Used in:
//   • Direct Sale tab (admin)  → switches selling price on cart + catalog
//   • Supervisor page         → same behavior, B+ tier uses backend's wholesale_price
//
// Visual:
//   • A+ → royal blue circle (Crown icon)
//   • B+ → royal purple circle (Sparkles icon)
//   • Active tier is filled + scaled · inactive is outline only
import { Crown, Sparkles } from "lucide-react";

const TIER_COLORS = {
  A_plus: { fill: "linear-gradient(135deg, #1E3A5F 0%, #3B82F6 100%)", border: "#1E3A5F", text: "#1E3A5F", label: "A+", icon: Crown },
  B_plus: { fill: "linear-gradient(135deg, #4C1D95 0%, #A855F7 100%)", border: "#7C3AED", text: "#7C3AED", label: "B+", icon: Sparkles },
};

const TierToggle = ({ value, onChange, size = "md" }) => {
  const dim = size === "sm" ? 44 : size === "lg" ? 64 : 52;
  return (
    <div className="flex items-center gap-2" data-testid="tier-toggle">
      {Object.entries(TIER_COLORS).map(([id, t]) => {
        const Icon = t.icon;
        const active = value === id;
        return (
          <button
            key={id}
            type="button"
            onClick={() => onChange(id)}
            data-testid={`tier-toggle-${id}`}
            aria-label={`زبائن ${t.label}`}
            title={`عرض الأسعار بمستوى ${t.label}`}
            className="rounded-full transition-all active:scale-90 shrink-0 relative"
            style={{
              width: dim,
              height: dim,
              background: active ? t.fill : "white",
              border: `3px solid ${t.border}`,
              boxShadow: active ? `0 6px 18px ${t.border}55` : "none",
              transform: active ? "scale(1.1)" : "scale(1)",
            }}
          >
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-0.5">
              <Icon
                className="opacity-90"
                style={{ width: dim * 0.32, height: dim * 0.32, color: active ? "white" : t.text }}
              />
              <span
                className="font-black"
                style={{ fontSize: dim * 0.28, lineHeight: 1, color: active ? "white" : t.text }}
                dir="ltr"
              >
                {t.label}
              </span>
            </div>
          </button>
        );
      })}
    </div>
  );
};

// Resolve the *effective* selling price for a product based on the chosen tier.
// A+ → product.price (regular selling price)
// B+ → product.wholesale_price (if set & > 0), otherwise falls back to product.price
export const priceForTier = (product, tier) => {
  if (!product) return 0;
  if (tier === "B_plus") {
    const w = Number(product.wholesale_price);
    if (isFinite(w) && w > 0) return w;
  }
  return Number(product.price || 0);
};

// B+ per-piece surcharge — REMOVED entirely on Jul 14, 2026 by admin request.
// B+ customers pay their wholesale price straight with NO extra add-ons.
// Kept as a no-op function to avoid touching every caller in the codebase.
export const bPlusLooseSurcharge = () => 0;

// Total price for one cart line item including B+ piece surcharge.
export const lineTotal = (product, quantity, tier, overridePrice = null) => {
  const base = overridePrice != null ? Number(overridePrice) : priceForTier(product, tier);
  const qty = Number(quantity || 0);
  return base * qty + bPlusLooseSurcharge(product, qty, tier);
};

export default TierToggle;
